const { qs, qsa, accept, build } = require('../../libs/qs')
const uuid = require('uuid').v4
const ctrlz = require('../common/ctrlz')
const { faderH, dualrange } = ctrlz
const engine = require('../common/engine')
const fxs = require('../common/fx')
const Sortable = require('sortablejs')
const { FFmpeg } = require('@ffmpeg/ffmpeg')
const { fetchFile } = require('@ffmpeg/util')

// ---- Bucket integration ----
const initLegacyBucket = require('./legacy-bucket-integration.js');

// Detect supported MediaRecorder MIME types
function getSupportedMimeType() {
    const types = [
        'video/webm;codecs=vp8',
        'video/webm;codecs=vp9',
        'video/webm',
        'video/mp4',
        'video/mp4;codecs=avc1'
    ]
    
    for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
            return type
        }
    }
    
    return 'video/webm'
}

class ImageProcessor {
    constructor() {
        this.worker = null;
        this.ready = false;
        this.pendingRequests = new Map();
        this.requestId = 0;
        this.isWorkerSupported = typeof Worker !== 'undefined' && typeof OffscreenCanvas !== 'undefined';
    }

    _createWorker() {
        const blob = new Blob([__WORKER_SOURCE__], { type: 'application/javascript' });
        const workerUrl = URL.createObjectURL(blob);
        this.worker = new Worker(workerUrl);
        this.worker.onmessage = (e) => this.handleMessage(e.data);
        this.worker.onerror = (e) => {
            console.error('[ImageProcessor] Worker error:', e);
            this.ready = false;
        };
    }

    _waitUntilReady(maxAttempts = 100) {
        return new Promise((resolve) => {
            let attempts = 0;
            const checkReady = () => {
                if (this.ready) {
                    resolve(true);
                    return;
                }
                attempts++;
                if (attempts > maxAttempts) {
                    resolve(false);
                    return;
                }
                setTimeout(checkReady, 50);
            };
            checkReady();
        });
    }

    async init() {
        if (!this.isWorkerSupported) return false;
        return new Promise((resolve) => {
            try {
                this._createWorker();
                this._waitUntilReady().then(resolve);
            } catch (error) {
                resolve(false);
            }
        });
    }

    pendingCount() {
        return this.pendingRequests.size;
    }

    async cancelCurrentPipeline(reason = 'Pipeline cancelled by new request') {
        this.pendingRequests.forEach(({ reject }) => {
            try {
                reject(new Error(reason));
            } catch (_) {
            }
        });
        this.pendingRequests.clear();

        if (this.worker) {
            try {
                this.worker.terminate();
            } catch (_) {
            }
            this.worker = null;
        }

        this.ready = false;

        if (!this.isWorkerSupported) {
            return false;
        }

        try {
            this._createWorker();
            this.ready = await this._waitUntilReady();
        } catch (_) {
            this.ready = false;
        }

        return this.ready;
    }

    handleMessage(data) {
        const { type, id, success, data: resultData, error, width, height } = data;
        if (type === 'ready') {
            this.ready = true;
            return;
        }
        if ((type === 'fxResult' || type === 'batchResult') && id !== undefined) {
            const pending = this.pendingRequests.get(id);
            if (pending) {
                if (success) pending.resolve({ data: resultData, width, height });
                else pending.reject(new Error(error));
                this.pendingRequests.delete(id);
            }
        }
    }

    async processFx(fxName, imageData, width, height, ...args) {
        if (!this.ready) throw new Error('Worker not ready');
        const id = ++this.requestId;
        const canvasData = new Uint8ClampedArray(imageData);
        return new Promise((resolve, reject) => {
            this.pendingRequests.set(id, { resolve, reject });
            this.worker.postMessage({ type: 'processFx', id, payload: { fxName, canvasData: canvasData.buffer, width, height, args } }, [canvasData.buffer]);
            setTimeout(() => {
                if (this.pendingRequests.has(id)) {
                    this.pendingRequests.delete(id);
                    reject(new Error(`Timeout: ${fxName}`));
                }
            }, 120000);
        });
    }

    async processBatch(fxList, imageData, width, height) {
        if (!this.ready) throw new Error('Worker not ready');
        const id = ++this.requestId;
        const canvasData = new Uint8ClampedArray(imageData);
        return new Promise((resolve, reject) => {
            this.pendingRequests.set(id, { resolve, reject });
            this.worker.postMessage({ type: 'processBatch', id, payload: { fxList, canvasData: canvasData.buffer, width, height } }, [canvasData.buffer]);
            setTimeout(() => {
                if (this.pendingRequests.has(id)) {
                    this.pendingRequests.delete(id);
                    reject(new Error(`Batch timeout`));
                }
            }, 120000);
        });
    }

    isReady() {
        return this.ready;
    }
}

window.ImageProcessor = ImageProcessor;

class FrameStackProcessor {
    constructor() {
        this.ready = false;
    }

    async init() {
        try {
            await engine.initWorker();
            this.ready = engine.workerReady && !!engine.imageProcessor;
        } catch (e) {
            this.ready = false;
        }
        return this.ready;
    }

    isReady() { 
        return this.ready;
    }
}

window.FrameStackProcessor = FrameStackProcessor;

// Use localStorage theme if available, otherwise fall back to server-provided window.Theme
let currentTheme;
try {
  const storedTheme = localStorage.getItem('currentTheme');
  if (storedTheme) {
    currentTheme = JSON.parse(storedTheme);
  }
} catch (e) {}
if (!currentTheme) {
  currentTheme = window.Theme;
}

const themeColors = Object.values(currentTheme.data)
const themeRgb = themeColors.map((el) => {
    return { r: parseInt(el.slice(1, 3), 16), g: parseInt(el.slice(3, 5), 16), b: parseInt(el.slice(5, 7), 16) }
})

const processingIndicator = { icon: '|', frames: ['|', '/', '-', '\\'], frameIndex: 0, interval: null }
let isFxProcessing = false

let stopTimeout = null

const ensureToastContainer = () => {
    let container = document.getElementById('ug-toast-container')
    if (!container) {
        container = document.createElement('div')
        container.id = 'ug-toast-container'
        container.className = 'ug-toast-container'
        document.body.appendChild(container)
    }
    return container
}

const showTransientToast = (message) => {
    const container = ensureToastContainer()
    const toast = document.createElement('div')
    toast.className = 'ug-toast ug-toast-success'
    toast.textContent = String(message || 'Done')
    container.appendChild(toast)
    setTimeout(() => toast.classList.add('ug-toast-show'), 10)
    setTimeout(() => {
        toast.classList.remove('ug-toast-show')
        setTimeout(() => toast.remove(), 220)
    }, 1800)
}

function stopProcessingAnimation() {
    if (stopTimeout) return
    stopTimeout = setTimeout(() => {
        isFxProcessing = false
        if (processingIndicator.interval) {
            clearInterval(processingIndicator.interval)
            processingIndicator.interval = null
        }
        const fxProcessingEl = qs('#fx-processing')
        if (fxProcessingEl) fxProcessingEl.style.display = 'none'
        const outcanvas = qs('#outputcanvas')
        if (outcanvas) outcanvas.style.opacity = '1'
        stopTimeout = null
    }, 200)
}

function showProcessingNow() {
    if (stopTimeout) {
        clearTimeout(stopTimeout)
        stopTimeout = null
    }
    isFxProcessing = true
    const fxProcessingEl = qs('#fx-processing')
    if (fxProcessingEl) fxProcessingEl.style.display = 'block'
    const outcanvas = qs('#outputcanvas')
    if (outcanvas) outcanvas.style.opacity = '0.4'
    if (!processingIndicator.interval) {
        processingIndicator.interval = setInterval(() => {
            if (fxProcessingEl) {
                fxProcessingEl.textContent = processingIndicator.frames[processingIndicator.frameIndex]
                processingIndicator.frameIndex = (processingIndicator.frameIndex + 1) % processingIndicator.frames.length
            }
        }, 100)
    }
}

engine.on('start', () => showProcessingNow())
engine.on('complete', () => stopProcessingAnimation())
engine.on('error', () => showTransientToast('FX processing failed'))

window.CONTROLZ = {}
window.themeColors = themeColors
window.currentTheme = currentTheme
window.themeRgb = themeRgb
window.lfoz = {}
window.lfoindex = 0
window.lfolinks = {}
window.bandlinks = {}
window.macrolinks = {}
window.midilinks = {}

let processor = null
let frames = []
let fxStack = []
let renderedFrames = []
let isRendering = false
let renderedBlob = null
let ffmpeg = null
let selectedFrame = null
let isVideoPreviewMode = false
let renders = []
let activeTab = 'preview'
let copiedFrame = null

const easingFunctions = {
    'linear': t => t,
    'ease-in': t => t * t,
    'ease-out': t => t * (2 - t),
    'ease-in-out': t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}

function interpolateValue(start, end, progress, easing = 'linear') {
    const easedProgress = easingFunctions[easing] ? easingFunctions[easing](progress) : progress
    return start + (end - start) * easedProgress
}

document.addEventListener('DOMContentLoaded', async () => {
    processor = new FrameStackProcessor()
    await processor.init()
    
    // Initialize worker for FX processing
    try {
        await engine.initWorker()
    } catch (err) {
        console.error('[FRAMESTACK] Worker initialization failed:', err)
    }

    initFXList()
    initEventListeners()
    initDragDrop()
    initSortable()
    initFFmpeg()
    
    // Initialize preview tab as active
    switchTab('preview')
})

async function initFFmpeg() {
    try {
        ffmpeg = new FFmpeg()
        ffmpeg.on('log', ({ message }) => {

            // Parse FFmpeg progress messages
            if (message.includes('frame=')) {
                const frameMatch = message.match(/frame=\s*(\d+)/)
                const fpsMatch = message.match(/fps=\s*([\d.]+)/)
                const timeMatch = message.match(/time=\s*([\d:.]+)/)

                if (frameMatch) {
                    const currentFrame = parseInt(frameMatch[1])
                    // Update progress based on frame count
                    if (window.extractProgressCallback) {
                        window.extractProgressCallback(currentFrame)
                    }
                }
            }
        })
        await ffmpeg.load({
            coreURL: '/ffmpeg/ffmpeg-core.module.js',
            wasmURL: '/ffmpeg/ffmpeg-core.wasm',
        })
    } catch (e) {
    }
}

// Reset FFmpeg instance after a WASM crash (RuntimeError: index out of bounds)
// This terminates the corrupted instance and creates a fresh one
async function resetFFmpeg() {
    try { if (ffmpeg) ffmpeg.terminate() } catch (_) {}
    ffmpeg = null
    await initFFmpeg()
}

function initFXList() {
    const fxlist = qs('#fxlist')
    if (!fxlist) return

    fxs.forEach((fx, index) => {
        const li = document.createElement('li')
        li.textContent = fx.name.replace(/-/g, ' ')
        li.dataset.index = index
        li.addEventListener('click', () => addToStack(index))
        fxlist.appendChild(li)
    })

    const randomFxBtn = qs('#random-fx-btn')
    if (randomFxBtn) {
        randomFxBtn.addEventListener('click', () => {
            const randomIndex = Math.floor(Math.random() * fxs.length)
            addToStack(randomIndex)
        })
    }
}

function addToStack(fxIndex) {
    const fx = fxs[fxIndex]
    if (!fx) return

    const fxInstance = {
        id: uuid(),
        fxIndex: fxIndex,
        name: fx.name,
        enabled: true,
        params: {}
    }

    fx.inputs.forEach(input => {
        fxInstance.params[input.name] = { ...input }
    })

    fxStack.push(fxInstance)
    renderFXStack()
}

function randomizeAllFXParams() {
    fxStack.forEach(fxInstance => {
        if (fxInstance.params) {
            Object.values(fxInstance.params).forEach(param => {
                if (param.type === 'knob') {
                    const range = param.max - param.min
                    const randomValue = param.min + Math.random() * range
                    param.value = Math.round(randomValue / param.step) * param.step
                    param.value = Math.max(param.min, Math.min(param.max, param.value))
                }
            })
        }
    })
    renderFXStack()
    updateOutput()
}

function renderFXStack() {
    const fxbox = qs('#fxbox')
    if (!fxbox) return

    fxbox.innerHTML = ''

    fxStack.forEach((fxInstance, stackIndex) => {
        const hold = document.createElement('li')
        hold.className = 'fxhold'
        hold.dataset.id = fxInstance.id

        const box = document.createElement('div')
        box.className = 'fxbox'

        const handle = document.createElement('div')
        handle.className = 'handle'
        handle.innerHTML = `
            <span class="fxname">${fxInstance.name.replace(/-/g, ' ')}</span>
        `

        const handle2 = document.createElement('div')
        handle2.className = 'topfx'
        
        const switchid = 'fx-switch-' + fxInstance.id
        const onoff = document.createElement('input')
        onoff.id = switchid
        onoff.checked = fxInstance.enabled
        onoff.classList.add('switch')
        onoff.type = 'checkbox'
        
        const label = document.createElement('label')
        label.classList.add('switchlabel')
        label.setAttribute('for', switchid)
        
        onoff.addEventListener('change', (e) => {
            fxInstance.enabled = e.target.checked
            updateOutput()
        })
        
        const delbox = document.createElement('div')
        delbox.classList.add('btnborder')
        const del = document.createElement('button')
        del.classList.add('btnctrl')
        del.classList.add('icn')
        del.innerHTML = ''
        delbox.appendChild(del)
        
        del.addEventListener('click', (e) => {
            e.stopPropagation()
            fxStack = fxStack.filter(f => f.id !== fxInstance.id)
            renderFXStack()
        })
        
        handle2.appendChild(onoff)
        handle2.appendChild(label)
        handle2.appendChild(delbox)
        handle.appendChild(handle2)

        box.appendChild(handle)

        const content = document.createElement('div')
        content.className = 'fxcontent ctrlgrid'

        fxInstance.params && Object.entries(fxInstance.params).forEach(([paramName, param]) => {
            if (param.type === 'knob') {
                const inp = document.createElement('input')
                inp.type = 'number'
                inp.className = 'ctrlinput'
                inp.dataset.label = paramName
                inp.dataset.nocurve = '1'
                inp.dataset.param = paramName
                inp.value = param.value
                inp.min = param.min
                inp.max = param.max
                inp.step = param.step
                content.appendChild(inp)

                inp.addEventListener('change', (e) => {
                    let val = parseFloat(e.target.value)
                    if (isNaN(val)) val = param.min
                    val = Math.max(param.min, Math.min(param.max, val))
                    param.value = val
                    updateOutput()
                })

                try { faderH(inp) } catch (e) { console.warn('[framestack] faderH failed:', e) }
            }
        })

        box.appendChild(content)
        hold.appendChild(box)
        fxbox.appendChild(hold)
    })

    if (fxStack.length > 0) {
        updateOutput()
    }
}

function initSortable() {
    const fxbox = qs('#fxbox')
    if (!fxbox || typeof Sortable === 'undefined') return

    new Sortable(fxbox, {
        animation: 150,
        ghostClass: 'sortable-ghost',
        handle: '.handle',
        onEnd: (evt) => {
            const item = fxStack.splice(evt.oldIndex, 1)[0]
            fxStack.splice(evt.newIndex, 0, item)
        }
    })

    const framesList = qs('#frames-list')
    if (framesList && typeof Sortable !== 'undefined') {
        new Sortable(framesList, {
            animation: 150,
            ghostClass: 'sortable-ghost',
            onEnd: (evt) => {
                const item = frames.splice(evt.oldIndex, 1)[0]
                frames.splice(evt.newIndex, 0, item)
                if (selectedFrame) {
                    const newSelectedIndex = frames.findIndex(f => f.id === selectedFrame.id)
                    if (newSelectedIndex !== -1) {
                        // Update selected frame reference
                        selectedFrame = frames[newSelectedIndex]
                    }
                }
            }
        })
    }
}

function initEventListeners() {
    const frameInput = qs('#frame-input')
    const clearFramesBtn = qs('#clear-frames')
    const clearFxBtn = qs('#clear-fx')
    const randomizeBtn = qs('#randomize-all')
    const renderBtn = qs('#render-btn')
    const formatSelect = qs('#output-format')

    frameInput?.addEventListener('change', handleFrameFiles)

    clearFramesBtn?.addEventListener('click', () => {
        frames = []
        renderedFrames = []
        renderFramesList()
        clearOutput()
    })

    clearFxBtn?.addEventListener('click', () => {
        fxStack = []
        renderFXStack()
    })

    randomizeBtn?.addEventListener('click', () => {
        randomizeAllFXParams()
    })

    renderBtn?.addEventListener('click', () => {
        renderVideo()
    })

    formatSelect?.addEventListener('change', () => {
        if (frames.length > 0) {
            updateOutput()
        }
    })

    // Aspect ratio change listener
    const aspectRatioSelect = qs('#aspect-ratio')
    aspectRatioSelect?.addEventListener('change', () => {
        if (frames.length > 0) {
            updateOutput()
        }
    })

    // Copy/Paste keyboard shortcuts
    document.addEventListener('keydown', (e) => {
        // Ctrl+C - Copy selected frame
        if (e.ctrlKey && e.key === 'c' && selectedFrame) {
            e.preventDefault()
            copiedFrame = { ...selectedFrame }
            showTransientToast('Frame copied')
        }
        
        // Ctrl+V - Paste copied frame
        if (e.ctrlKey && e.key === 'v' && copiedFrame) {
            e.preventDefault()
            const selectedIndex = frames.findIndex(f => f.id === selectedFrame?.id)
            
            // Create a new frame with a new ID but same data
            const newId = uuid()
            const newFrame = {
                ...copiedFrame,
                id: newId,
                name: `${copiedFrame.name}_copy`
            }
            
            if (selectedIndex !== -1) {
                // Insert after selected frame
                frames.splice(selectedIndex + 1, 0, newFrame)
            } else if (frames.length > 0) {
                // Append to end if no frame selected
                frames.push(newFrame)
            } else {
                // Add as first frame if list is empty
                frames.push(newFrame)
            }
            
            renderFramesList()
            if (frames.length > 0) {
                updateOutput()
            }
            showTransientToast('Frame pasted')
        }
    })

    // Tab switching using event delegation
    const tabsContainer = qs('#output-tabs')
    tabsContainer.addEventListener('click', (e) => {
        const tabItem = e.target.closest('.tab-item')
        if (!tabItem) return
        
        // Don't switch tab if clicking on close button
        if (e.target.classList.contains('tab-close')) return
        
        const tabId = tabItem.dataset.tab
        if (!tabId) return

        switchTab(tabId)
    })

    // Video download button in controls
    const videoDownloadBtn = qs('#videoDownload')
    videoDownloadBtn?.addEventListener('click', () => {
        downloadVideo()
    })

    // Save preview frame button
    const savePreviewBtn = qs('#save-preview-btn')
    savePreviewBtn?.addEventListener('click', () => {
        savePreviewFrame()
    })

    // Click on preview canvas to renew FX
    const canvas = qs('#outputcanvas')
    canvas?.addEventListener('click', () => {
        updateOutput()
    })
}

function switchTab(tabId) {
    activeTab = tabId
    
    // Update tab active states
    const tabs = qsa('.tab-item')
    tabs.forEach(tab => {
        if (tab.dataset.tab === tabId) {
            tab.classList.add('active')
        } else {
            tab.classList.remove('active')
        }
    })
    
    // Show/hide content based on tab
    const canvas = qs('#outputcanvas')
    const videoContainer = qs('#video-preview-container')
    const videoControls = qs('#videoControls')
    const savePreviewContainer = qs('.preview-save-container')
    
    if (tabId === 'preview') {
        canvas.style.display = 'block'
        videoContainer.style.display = 'none'
        videoControls?.classList.remove('visible')
        savePreviewContainer?.classList.remove('hidden')
        updateOutput()
    } else {
        savePreviewContainer?.classList.add('hidden')
        const render = renders.find(r => r.id === tabId)
        if (render && render.blob) {
            canvas.style.display = 'none'
            videoContainer.style.display = 'flex'
            videoControls?.classList.add('visible')
            
            const video = qs('#video-preview')
            if (video.src) {
                URL.revokeObjectURL(video.src)
            }
            video.src = URL.createObjectURL(render.blob)
            video.loop = true
            video.play().catch(() => {})
            initVideoPlayer()
        }
    }
}

function closeRenderTab(renderId) {
    const renderIndex = renders.findIndex(r => r.id === renderId)
    if (renderIndex === -1) return
    
    // Remove from renders array
    const render = renders.splice(renderIndex, 1)[0]
    
    // Revoke blob URL
    if (render.blob) {
        URL.revokeObjectURL(render.blob)
    }
    
    // Remove tab from DOM
    const tab = qs(`.tab-item[data-tab="${renderId}"]`)
    if (tab) {
        tab.remove()
    }
    
    // If closing active tab, switch to preview
    if (activeTab === renderId) {
        switchTab('preview')
    }
    
    // Renumber remaining renders
    const renderTabs = qsa('.tab-item[data-tab^="render-"]')
    renderTabs.forEach((tab, index) => {
        const span = tab.querySelector('span:first-child')
        if (span) {
            span.textContent = 'Render ' + (index + 1)
        }
    })
}

function initDragDrop() {
    const dropzone = qs('#frames-dropzone')
    if (!dropzone) return

    const handleDragOver = (e) => {
        e.preventDefault()
        dropzone.classList.add('dragover')
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        dropzone.classList.remove('dragover')
    }

    const handleDrop = (e) => {
        e.preventDefault()
        dropzone.classList.remove('dragover')
        const files = Array.from(e.dataTransfer.files)
        const imageFiles = files.filter(f => f.type.startsWith('image/'))
        const videoFiles = files.filter(f => f.type.startsWith('video/'))
        
        processImageFiles(imageFiles)
        processVideoFiles(videoFiles)
    }

    dropzone.addEventListener('dragover', handleDragOver)
    dropzone.addEventListener('dragleave', handleDragLeave)
    dropzone.addEventListener('drop', handleDrop)
    dropzone.addEventListener('click', () => {
        const input = qs('#frame-input')
        input?.click()
    })
}

function handleFrameFiles(e) {
    const files = Array.from(e.target.files)
    const imageFiles = files.filter(f => f.type.startsWith('image/'))
    const videoFiles = files.filter(f => f.type.startsWith('video/'))
    
    processImageFiles(imageFiles)
    processVideoFiles(videoFiles)
    
    e.target.value = ''
}

async function processImageFiles(files) {
    for (const file of files) {
        const id = uuid()
        const url = URL.createObjectURL(file)
        frames.push({ id, name: file.name, file, url })
    }
    renderFramesList()
    if (frames.length > 0) {
        updateOutput()
    }
}

async function processVideoFiles(files) {
    if (!ffmpeg) {
        showTransientToast('FFmpeg not loaded, cannot process videos')
        return
    }
    
    const extractModal = qs('#video-extract-modal')
    const extractProgressBar = qs('#extract-progress-bar')
    const extractPercent = qs('#extract-percent')
    const extractVideoName = qs('#extract-video-name')
    
    if (extractModal) extractModal.style.display = 'flex'
    
    try {
        for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
            const file = files[fileIndex]
            
            if (extractVideoName) extractVideoName.textContent = `Processing: ${file.name} (${fileIndex + 1}/${files.length})`
            
            try {
                const inputName = `input_${Date.now()}_${file.name}`
                const outputPattern = `frame_%d.png`
                
                if (extractProgressBar) extractProgressBar.style.width = '5%'
                if (extractPercent) extractPercent.textContent = '5%'
                
                await ffmpeg.writeFile(inputName, await fetchFile(file))
                
                if (extractProgressBar) extractProgressBar.style.width = '10%'
                if (extractPercent) extractPercent.textContent = '10%'
                
                // Get video info to get total frames
                let totalFrames = 0
                try {
                    await ffmpeg.exec(['-i', inputName, '-f', 'null', '-'])
                } catch (e) {
                    // FFmpeg outputs info to stderr, we'll estimate
                }
                
                if (extractProgressBar) extractProgressBar.style.width = '15%'
                if (extractPercent) extractPercent.textContent = '15%'
                
                // Set up progress callback for this extraction
                let currentFrame = 0
                window.extractProgressCallback = (frame) => {
                    currentFrame = frame
                    const progress = 15 + (frame / 300) * 80 // Estimate 300 frames max for progress
                    if (extractProgressBar) extractProgressBar.style.width = `${Math.min(progress, 95)}%`
                    if (extractPercent) extractPercent.textContent = `${Math.min(Math.round(progress), 95)}%`
                }
                
                await ffmpeg.exec([
                    '-i', inputName,
                    '-vf', 'fps=30',
                    outputPattern
                ])
                
                if (extractProgressBar) extractProgressBar.style.width = '50%'
                if (extractPercent) extractPercent.textContent = '50%'
                
                // Clear progress callback
                window.extractProgressCallback = null
                
                // Read all extracted frames
                let frameIndex = 1
                while (true) {
                    try {
                        const frameName = `frame_${frameIndex}.png`
                        const frameData = await ffmpeg.readFile(frameName)
                        const frameBlob = new Blob([frameData.buffer], { type: 'image/png' })
                        const frameUrl = URL.createObjectURL(frameBlob)
                        
                        const id = uuid()
                        frames.push({ 
                            id, 
                            name: `${file.name}_frame_${frameIndex}`, 
                            file: frameBlob, 
                            url: frameUrl 
                        })
                        
                        await ffmpeg.deleteFile(frameName)
                        frameIndex++
                    } catch (e) {
                        // No more frames
                        break
                    }
                }
                
                await ffmpeg.deleteFile(inputName)
                
            } catch (e) {
                console.error('[FRAMESTACK] Error extracting frames from video:', e)
                showTransientToast('Error extracting frames from video')
            }
        }
        
        if (extractProgressBar) extractProgressBar.style.width = '100%'
        if (extractPercent) extractPercent.textContent = '100%'
        
        setTimeout(() => {
            if (extractModal) extractModal.style.display = 'none'
        }, 500)
        
    } catch (e) {
        console.error('[FRAMESTACK] Error processing video files:', e)
        if (extractModal) extractModal.style.display = 'none'
    }
    
    renderFramesList()
    if (frames.length > 0) {
        updateOutput()
    }
}

function renderFramesList() {
    const list = qs('#frames-list')
    const count = qs('.frames-count')

    if (count) count.textContent = `${frames.length} frame${frames.length !== 1 ? 's' : ''}`

    if (!list) return
    list.innerHTML = ''

    frames.forEach((frame, index) => {
        const li = document.createElement('li')
        li.className = 'frame-item'
        if (selectedFrame && selectedFrame.id === frame.id) {
            li.classList.add('selected')
        }

        const thumb = document.createElement('img')
        thumb.className = 'frame-thumb'
        thumb.src = frame.url
        thumb.alt = frame.name

        const info = document.createElement('div')
        info.className = 'frame-info'
        info.innerHTML = `
            <span class="frame-name">${frame.name}</span>
            <span class="frame-index">#${index + 1}</span>
        `

        const deleteBtn = document.createElement('div')
        deleteBtn.className = 'btnborder frame-delete'
        deleteBtn.innerHTML = '<button class="btnctrl icn remove-frame" title="Remove"></button>'
        deleteBtn.querySelector('button').addEventListener('click', (e) => {
            e.stopPropagation()
            URL.revokeObjectURL(frame.url)
            frames = frames.filter(f => f.id !== frame.id)
            if (selectedFrame && selectedFrame.id === frame.id) {
                selectedFrame = null
            }
            renderedFrames = renderedFrames.filter(rf => rf.frameId !== frame.id)
            renderFramesList()
        })

        li.addEventListener('click', () => {
            selectedFrame = frame
            renderFramesList()
            updateOutput()
        })

        li.appendChild(thumb)
        li.appendChild(info)
        li.appendChild(deleteBtn)
        list.appendChild(li)
    })
}

async function updateOutput() {
    if (frames.length === 0) return

    showProcessingNow()

    try {
        const frameToPreview = selectedFrame || frames[0]
        const response = await fetch(frameToPreview.url)
        const blob = await response.blob()
        const bitmap = await createImageBitmap(blob)

        const canvas = qs('#outputcanvas')
        if (!canvas) return

        const targetRes = qs('#resolution')?.value || 'original'
        let width = bitmap.width
        let height = bitmap.height

        if (targetRes !== 'original') {
            const maxDim = parseInt(targetRes)
            const ratio = Math.min(maxDim / width, maxDim / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
        }

        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(bitmap, 0, 0, width, height)

        if (fxStack.length > 0) {
            const enabledFx = fxStack.filter(fx => fx.enabled)

            if (enabledFx.length > 0) {
                for (const fx of enabledFx) {
                    const fxDef = fxs[fx.fxIndex]
                    const params = Object.values(fx.params).map(p => p.value)
                    if (fxDef && fxDef.fn) {
                        // Convert numeric params to string for effects that expect strings
                        // Effects with "mode" parameters typically expect string values
                        const hasModeParam = fxDef.inputs && fxDef.inputs.some(input => input.name === 'mode')
                        const convertedParams = params.map(p => 
                            hasModeParam ? String(p) : p
                        )
                        await fxDef.fn(canvas, ctx, ...convertedParams)
                    }
                }
            }
        }

        bitmap.close()
    } catch (err) {
        console.error('Preview update failed:', err)
    }

    stopProcessingAnimation()
}

function clearOutput() {
    const canvas = qs('#outputcanvas')
    if (canvas) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
    renderedFrames = []
    renderedBlob = null
    const downloadBtn = qs('#download-btn')
    if (downloadBtn) downloadBtn.disabled = true
}

async function generatePreview() {
    if (frames.length < 2) {
        showTransientToast('Need at least 2 frames')
        return
    }

    showProcessingNow()

    try {
        await renderAllFrames()

        const videoContainer = qs('#video-preview-container')
        const video = qs('#video-preview')
        const canvas = qs('#outputcanvas')

        if (videoContainer && video && canvas) {
            videoContainer.style.display = 'flex'
            canvas.style.display = 'none'
            initVideoPlayer()

            const format = qs('#output-format')?.value || 'webm'
            const mimeType = format === 'gif' ? 'image/gif' :
                            format === 'mp4' ? 'video/mp4' : 'video/webm'

            const stream = canvas.captureStream()
            const mediaRecorder = new MediaRecorder(stream, { mimeType })
            const chunks = []

            mediaRecorder.ondataavailable = (e) => chunks.push(e.data)
            mediaRecorder.onstop = () => {
                renderedBlob = new Blob(chunks, { type: mimeType })
                video.src = URL.createObjectURL(renderedBlob)
                video.play()
            }

            const frameTime = parseInt(qs('#frametime')?.value || '333')
            const targetRes = qs('#resolution')?.value || 'original'

            let width = renderedFrames[0]?.width || 640
            let height = renderedFrames[0]?.height || 480

            if (targetRes !== 'original') {
                const maxDim = parseInt(targetRes)
                const ratio = Math.min(maxDim / width, maxDim / height)
                width = Math.round(width * ratio)
                height = Math.round(height * ratio)
            }

            canvas.width = width
            canvas.height = height
            const ctx = canvas.getContext('2d')

            mediaRecorder.start()

            for (let i = 0; i < renderedFrames.length; i++) {
                const imgData = new ImageData(
                    renderedFrames[i].data,
                    renderedFrames[i].width,
                    renderedFrames[i].height
                )
                const tempCanvas = document.createElement('canvas')
                tempCanvas.width = renderedFrames[i].width
                tempCanvas.height = renderedFrames[i].height
                const tempCtx = tempCanvas.getContext('2d')
                tempCtx.putImageData(imgData, 0, 0)

                ctx.drawImage(tempCanvas, 0, 0, width, height)

                await new Promise(resolve => setTimeout(resolve, frameTime))
            }

            mediaRecorder.stop()

            const downloadBtn = qs('#download-btn')
            if (downloadBtn) downloadBtn.disabled = false
        }
    } catch (err) {
        console.error('Preview generation failed:', err)
        showTransientToast('Preview failed')
    }

    stopProcessingAnimation()
}

async function renderVideo() {
    if (frames.length < 2) {
        showTransientToast('Need at least 2 frames')
        return
    }

    if (isRendering) return
    isRendering = true

    const progressModal = qs('#render-progress-modal')
    const progressBar = qs('#render-progress-bar')
    const progressPercent = qs('#render-percent')
    const frameCount = qs('#render-frame-count')

    // Reset progress modal state
    if (progressBar) progressBar.style.width = '0%'
    if (progressPercent) progressPercent.textContent = '0%'
    if (frameCount) frameCount.textContent = '0 / 0'

    if (progressModal) progressModal.style.display = 'flex'

    try {
        await renderAllFrames()

        const format = qs('#output-format')?.value || 'webm'
        const frameTime = parseInt(qs('#frametime')?.value || '333') / 1000
        const targetRes = qs('#resolution')?.value || 'original'
        const targetAspectRatio = qs('#aspect-ratio')?.value || 'original'

        let width = renderedFrames[0]?.width || 640
        let height = renderedFrames[0]?.height || 480


        // Apply aspect ratio constraints first
        if (targetAspectRatio !== 'original') {
            const aspectRatios = {
                '16:9': 16 / 9,
                '1:1': 1,
                '9:16': 9 / 16
            }
            const targetRatio = aspectRatios[targetAspectRatio]
            const currentRatio = width / height

            if (currentRatio > targetRatio) {
                // Current is wider, adjust width
                width = Math.round(height * targetRatio)
            } else {
                // Current is taller, adjust height
                height = Math.round(width / targetRatio)
            }
        }

        // Then apply resolution constraints
        if (targetRes !== 'original') {
            const maxDim = parseInt(targetRes)
            const ratio = Math.min(maxDim / width, maxDim / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
        }

        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = width
        tempCanvas.height = height
        const ctx = tempCanvas.getContext('2d')

        const mimeType = format === 'gif' ? 'image/gif' : getSupportedMimeType()


        // Use FFmpeg for all formats (GIF, WebM, MP4)
        let retried = false
        try {
            // Load FFmpeg if needed
            if (!ffmpeg || !ffmpeg.loaded) {
                await ffmpeg.load({
                    coreURL: '/ffmpeg/ffmpeg-core.module.js',
                    wasmURL: '/ffmpeg/ffmpeg-core.wasm',
                })
            }
            
            // Calculate framerate from frame time (ms to fps)
            const frameTimeMs = parseInt(qs('#frametime')?.value || '333')
            const framerate = Math.round(1000 / frameTimeMs)
            
            // Write frames as PNG files to FFmpeg virtual filesystem
            for (let i = 0; i < renderedFrames.length; i++) {
                const imgData = new ImageData(
                    renderedFrames[i].data,
                    renderedFrames[i].width,
                    renderedFrames[i].height
                )
                const srcCanvas = document.createElement('canvas')
                srcCanvas.width = renderedFrames[i].width
                srcCanvas.height = renderedFrames[i].height
                const srcCtx = srcCanvas.getContext('2d')
                srcCtx.putImageData(imgData, 0, 0)
                
                // Resize to target dimensions
                ctx.drawImage(srcCanvas, 0, 0, width, height)
                
                // Convert canvas to PNG and write to FFmpeg
                const pngData = tempCanvas.toDataURL('image/png')
                const base64Data = pngData.split(',')[1]
                const binaryData = atob(base64Data)
                const uint8Array = new Uint8Array(binaryData.length)
                for (let j = 0; j < binaryData.length; j++) {
                    uint8Array[j] = binaryData.charCodeAt(j)
                }
                
                const frameName = `frame_${String(i).padStart(6, '0')}.png`
                await ffmpeg.writeFile(frameName, uint8Array)
                
                // Update progress
                if (frameCount) frameCount.textContent = `Preparing frames... ${i + 1}/${renderedFrames.length}`
                if (progressBar) progressBar.style.width = `${((i + 1) / renderedFrames.length) * 30}%`
                if (progressPercent) progressPercent.textContent = `${Math.round(((i + 1) / renderedFrames.length) * 30)}%`
            }
            
            
            const outputName = `output.${format}`
            
            // Delete output file if it exists to avoid conflicts
            try {
                await ffmpeg.deleteFile(outputName)
            } catch (e) {
                // File might not exist, that's fine
            }
            const totalDuration = renderedFrames.length * frameTime
            
            // Build FFmpeg command based on format
            let ffmpegArgs = [
                '-framerate', framerate.toString(),
                '-i', 'frame_%06d.png'
            ]
            
            if (format === 'gif') {
                // Simple GIF encoding
                ffmpegArgs = ffmpegArgs.concat([
                    '-y',
                    outputName
                ])
            } else if (format === 'webm') {
                // WebM encoding with VP8 (libvpx) — VP9 (libvpx-vp9) crashes in WASM
                ffmpegArgs = ffmpegArgs.concat([
                    '-c:v', 'libvpx',
                    '-crf', '30',
                    '-b:v', '1M',
                    '-pix_fmt', 'yuv420p',
                    '-progress', 'pipe:1',
                    '-y',
                    outputName
                ])
            } else {
                // MP4 encoding with H.264
                ffmpegArgs = ffmpegArgs.concat([
                    '-c:v', 'libx264',
                    '-preset', 'ultrafast',
                    '-crf', '28',
                    '-tune', 'fastdecode',
                    '-pix_fmt', 'yuv420p',
                    '-progress', 'pipe:1',
                    '-y',
                    outputName
                ])
            }
            
            // Track progress data
            let maxFramesSeen = 0
            let currentTime = 0
            let lastUpdateTime = 0
            const updateThrottle = 100
            
            // Set up progress logging
            ffmpeg.on('log', ({ message }) => {
                
                // Parse key=value pairs from progress output
                const kvMatch = message.match(/^(\w+)=(.*)$/)
                if (kvMatch) {
                    const key = kvMatch[1]
                    const value = kvMatch[2]
                    
                    switch(key) {
                        case 'frame':
                            const frame = parseInt(value)
                            if (frame > maxFramesSeen) maxFramesSeen = frame
                            break
                        case 'out_time':
                        case 'time':
                            const timeMatch = value.match(/(\d+):(\d+):(\d+\.?\d*)/)
                            if (timeMatch) {
                                const hours = parseInt(timeMatch[1])
                                const minutes = parseInt(timeMatch[2])
                                const seconds = parseFloat(timeMatch[3])
                                currentTime = hours * 3600 + minutes * 60 + seconds
                            }
                            break
                    }
                }
                
                // Also parse from standard output format
                const frameMatch = message.match(/frame=\s*(\d+)/)
                if (frameMatch) {
                    const frame = parseInt(frameMatch[1])
                    if (frame > maxFramesSeen) maxFramesSeen = frame
                }
                
                const timeMatch = message.match(/time=(\d+):(\d+):(\d+\.?\d*)/)
                if (timeMatch) {
                    const hours = parseInt(timeMatch[1])
                    const minutes = parseInt(timeMatch[2])
                    const seconds = parseFloat(timeMatch[3])
                    currentTime = hours * 3600 + minutes * 60 + seconds
                }
                
                // Throttle UI updates
                const now = Date.now()
                if (now - lastUpdateTime < updateThrottle) return
                lastUpdateTime = now
                
                // Calculate progress using best available metric
                let progress = 0
                if (totalDuration > 0 && currentTime > 0) {
                    progress = Math.min((currentTime / totalDuration) * 100, 100)
                } else if (maxFramesSeen > 0 && renderedFrames.length > 0) {
                    progress = Math.min((maxFramesSeen / renderedFrames.length) * 100, 100)
                }
                
                // Add 30% for frame preparation phase
                const displayProgress = 30 + (progress >= 95 ? 65 : progress * 0.65)
                
                // Update UI
                if (frameCount) {
                    if (progress >= 95) {
                        frameCount.textContent = 'Finalizing...'
                    } else if (totalDuration > 0 && currentTime > 0) {
                        frameCount.textContent = `Encoding ${format.toUpperCase()}... ${progress.toFixed(1)}%`
                    } else if (maxFramesSeen > 0) {
                        frameCount.textContent = `Encoding ${format.toUpperCase()}... Frame ${maxFramesSeen}/${renderedFrames.length}`
                    }
                }
                if (progressBar) progressBar.style.width = `${displayProgress}%`
                if (progressPercent) progressPercent.textContent = `${displayProgress.toFixed(0)}%`
            })
            
            // Encode PNG sequence to output format
            await ffmpeg.exec(ffmpegArgs)
            
            // Remove progress listener
            ffmpeg.off('log')
            
            // Update UI to show completion
            if (progressBar) progressBar.style.width = '100%'
            if (progressPercent) progressPercent.textContent = '100%'
            if (frameCount) frameCount.textContent = 'Complete!'
            
            const outputData = await ffmpeg.readFile(outputName)
            renderedBlob = new Blob([outputData.buffer], { type: mimeType })
            
            // Cleanup frame files
            for (let i = 0; i < renderedFrames.length; i++) {
                const frameName = `frame_${String(i).padStart(6, '0')}.png`
                try {
                    await ffmpeg.deleteFile(frameName)
                } catch (e) {
                    // Ignore errors during cleanup
                }
            }
            await ffmpeg.deleteFile(outputName)
        } catch (e) {
            console.error('[FRAMESTACK] FFmpeg encoding failed:', e)
            console.error('[FRAMESTACK] Error type:', e?.constructor?.name, 'name:', e?.name, 'message:', e?.message)
            ffmpeg.off('log')

            // Detect WASM crash (RuntimeError: index out of bounds) and retry once
            const isWasmCrash = (e?.name === 'RuntimeError') ||
                (e?.constructor?.name === 'RuntimeError') ||
                /RuntimeError|out of bounds/i.test(String(e?.message || e))
            if (isWasmCrash && !retried) {
                retried = true
                showTransientToast('FFmpeg crashed, retrying...')
                try {
                    await resetFFmpeg()
                    // Re-write frames and re-encode with fresh FFmpeg instance
                    for (let i = 0; i < renderedFrames.length; i++) {
                        const imgData = new ImageData(
                            renderedFrames[i].data,
                            renderedFrames[i].width,
                            renderedFrames[i].height
                        )
                        const srcCanvas = document.createElement('canvas')
                        srcCanvas.width = renderedFrames[i].width
                        srcCanvas.height = renderedFrames[i].height
                        const srcCtx = srcCanvas.getContext('2d')
                        srcCtx.putImageData(imgData, 0, 0)
                        ctx.drawImage(srcCanvas, 0, 0, width, height)
                        const pngData = tempCanvas.toDataURL('image/png')
                        const base64Data = pngData.split(',')[1]
                        const binaryData = atob(base64Data)
                        const uint8Array = new Uint8Array(binaryData.length)
                        for (let j = 0; j < binaryData.length; j++) {
                            uint8Array[j] = binaryData.charCodeAt(j)
                        }
                        const frameName = `frame_${String(i).padStart(6, '0')}.png`
                        await ffmpeg.writeFile(frameName, uint8Array)
                        if (frameCount) frameCount.textContent = `Re-preparing frames... ${i + 1}/${renderedFrames.length}`
                    }
                    await ffmpeg.exec(ffmpegArgs)
                    ffmpeg.off('log')
                    if (progressBar) progressBar.style.width = '100%'
                    if (progressPercent) progressPercent.textContent = '100%'
                    if (frameCount) frameCount.textContent = 'Complete!'
                    const outputData = await ffmpeg.readFile(outputName)
                    renderedBlob = new Blob([outputData.buffer], { type: mimeType })
                    // Cleanup
                    for (let i = 0; i < renderedFrames.length; i++) {
                        const frameName = `frame_${String(i).padStart(6, '0')}.png`
                        try { await ffmpeg.deleteFile(frameName) } catch (_) {}
                    }
                    await ffmpeg.deleteFile(outputName)
                } catch (retryErr) {
                    console.error('[FRAMESTACK] FFmpeg retry also failed:', retryErr)
                    ffmpeg.off('log')
                    showTransientToast(`${format.toUpperCase()} encoding failed`)
                }
            } else {
                showTransientToast(`${format.toUpperCase()} encoding failed`)
            }
        }

        if (renderedBlob) {
            const downloadBtn = qs('#download-btn')
            if (downloadBtn) downloadBtn.disabled = false
            
            const videoContainer = qs('#video-preview-container')
            const video = qs('#video-preview')
            const canvas = qs('#outputcanvas')
            
            if (videoContainer && video && canvas) {
                videoContainer.style.display = 'flex'
                canvas.style.display = 'none'
                
                if (video.src) {
                    URL.revokeObjectURL(video.src)
                }
                video.src = URL.createObjectURL(renderedBlob)
                video.loop = true
                video.play().catch(() => {})
                
                // Create new render tab
                const renderId = 'render-' + uuid()
                renders.push({
                    id: renderId,
                    blob: renderedBlob,
                    name: 'Render ' + (renders.length + 1)
                })
                
                // Add tab to tabs container
                const tabsContainer = qs('#output-tabs')
                if (tabsContainer) {
                    const tab = document.createElement('div')
                    tab.className = 'tab-item'
                    tab.dataset.tab = renderId
                    tab.innerHTML = `
                        <span>Render ${renders.length}</span>
                        <span class="tab-close" data-render="${renderId}">✕</span>
                    `
                    tabsContainer.appendChild(tab)
                    
                    // Add close button handler
                    const closeBtn = tab.querySelector('.tab-close')
                    closeBtn.addEventListener('click', (e) => {
                        e.stopPropagation()
                        closeRenderTab(renderId)
                    })
                }
                
                // Switch to new render tab
                switchTab(renderId)
            }
        } else {
            console.error('[FRAMESTACK] No blob created!')
        }

        showTransientToast('Render complete!')
    } catch (err) {
        console.error('Render failed:', err)
        showTransientToast('Render failed')
    }

    if (progressModal) progressModal.style.display = 'none'
    isRendering = false
}

async function renderAllFrames() {
    renderedFrames = []
    const targetRes = qs('#resolution')?.value || 'original'
    const targetAspectRatio = qs('#aspect-ratio')?.value || 'original'


    for (let i = 0; i < frames.length; i++) {
        const response = await fetch(frames[i].url)
        const blob = await response.blob()
        const bitmap = await createImageBitmap(blob)

        let width = bitmap.width
        let height = bitmap.height

        let sourceX = 0, sourceY = 0, sourceWidth = bitmap.width, sourceHeight = bitmap.height

        // Apply aspect ratio constraints first
        if (targetAspectRatio !== 'original') {
            const aspectRatios = {
                '16:9': 16 / 9,
                '1:1': 1,
                '9:16': 9 / 16
            }
            const targetRatio = aspectRatios[targetAspectRatio]
            const currentRatio = sourceWidth / sourceHeight

            if (currentRatio > targetRatio) {
                // Current is wider than target, crop width
                sourceWidth = Math.round(sourceHeight * targetRatio)
                sourceX = (bitmap.width - sourceWidth) / 2
            } else {
                // Current is taller than target, crop height
                sourceHeight = Math.round(sourceWidth / targetRatio)
                sourceY = (bitmap.height - sourceHeight) / 2
            }
        }

        // Set target dimensions
        width = sourceWidth
        height = sourceHeight

        // Then apply resolution constraints
        if (targetRes !== 'original') {
            const maxDim = parseInt(targetRes)
            const ratio = Math.min(maxDim / width, maxDim / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
        }


        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        
        // Draw with proper cropping and scaling
        ctx.drawImage(bitmap, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, width, height)

        if (fxStack.length > 0) {
            const enabledFx = fxStack.filter(fx => fx.enabled)

            if (enabledFx.length > 0) {
                for (const fx of enabledFx) {
                    const fxDef = fxs[fx.fxIndex]
                    const params = Object.values(fx.params).map(p => p.value)
                    if (fxDef && fxDef.fn) {
                        // Convert numeric params to string for effects that expect strings
                        // Effects with "mode" parameters typically expect string values
                        const hasModeParam = fxDef.inputs && fxDef.inputs.some(input => input.name === 'mode')
                        const convertedParams = params.map(p => 
                            hasModeParam ? String(p) : p
                        )
                        await fxDef.fn(canvas, ctx, ...convertedParams)
                    }
                }
            }

            const imageData = ctx.getImageData(0, 0, width, height)
            renderedFrames.push({
                frameId: frames[i].id,
                data: new Uint8ClampedArray(imageData.data),
                width,
                height
            })
        } else {
            const imageData = ctx.getImageData(0, 0, width, height)
            renderedFrames.push({
                frameId: frames[i].id,
                data: new Uint8ClampedArray(imageData.data),
                width,
                height
            })
        }

        bitmap.close()

        const progressBar = qs('#render-progress-bar')
        const progressPercent = qs('#render-percent')
        const frameCount = qs('#render-frame-count')

        if (frameCount) frameCount.textContent = `Frame ${i + 1}/${frames.length}`
        if (progressBar) progressBar.style.width = `${((i + 1) / frames.length) * 100}%`
        if (progressPercent) progressPercent.textContent = `${Math.round(((i + 1) / frames.length) * 100)}%`
    }

}

async function generateGIF(frames, width, height, delay) {
    return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')

        const encoder = new GIFEncoder(width, height)
        encoder.setDelay(delay)
        encoder.setRepeat(0)
        encoder.start()

        frames.forEach(frame => {
            const imgData = new ImageData(
                new Uint8ClampedArray(frame.data),
                frame.width,
                frame.height
            )
            const tempCanvas = document.createElement('canvas')
            tempCanvas.width = frame.width
            tempCanvas.height = frame.height
            const tempCtx = tempCanvas.getContext('2d')
            tempCtx.putImageData(imgData, 0, 0)
            ctx.drawImage(tempCanvas, 0, 0, width, height)
            encoder.addFrame(ctx)
        })

        encoder.finish()
        const blob = new Blob([encoder.getOutput()], { type: 'image/gif' })
        resolve(blob)
    })
}

function downloadVideo() {
    // Find the currently active render
    const activeRender = renders.find(r => r.id === activeTab)
    
    if (!activeRender || !activeRender.blob) {
        showTransientToast('No video to download')
        return
    }

    const format = qs('#output-format')?.value || 'webm'
    const extension = format === 'gif' ? 'gif' : format === 'mp4' ? 'mp4' : 'webm'
    const filename = `${activeRender.name.replace(/\s+/g, '_').toLowerCase()}_${Date.now()}.${extension}`

    const link = document.createElement('a')
    link.href = URL.createObjectURL(activeRender.blob)
    link.download = filename
    link.click()
    URL.revokeObjectURL(link.href)
}

function savePreviewFrame() {
    const canvas = qs('#outputcanvas')
    if (!canvas) {
        showTransientToast('No preview to save')
        return
    }

    const frameToPreview = selectedFrame || frames[0]
    if (!frameToPreview) {
        showTransientToast('No frame to save')
        return
    }

    const filename = `framestack_preview_${Date.now()}.png`
    
    const link = document.createElement('a')
    link.href = canvas.toDataURL('image/png')
    link.download = filename
    link.click()
    showTransientToast('Frame saved')
}

// Video player controls
let videoPlayer = null

function initVideoPlayer() {
    const video = qs('#video-preview')
    const controlsContainer = qs('#videoControls')
    if (!video || !controlsContainer) return

    videoPlayer = {
        video,
        controls: {
            seekBar: qs('#videoSeekBar'),
            seekFill: qs('#videoSeekFill'),
            playPauseBtn: qs('#videoPP'),
            stopBtn: qs('#videoStop'),
            currentTime: qs('#videoCurTime'),
            duration: qs('#videoDurTime'),
            speedSelect: qs('#videoSpeed')
        },
        isDragging: false
    }

    // Play/Pause
    videoPlayer.controls.playPauseBtn?.addEventListener('click', () => {
        if (videoPlayer.video.paused) {
            videoPlayer.video.play()
            videoPlayer.controls.playPauseBtn.textContent = '⏸'
            videoPlayer.controls.playPauseBtn.classList.add('playing')
        } else {
            videoPlayer.video.pause()
            videoPlayer.controls.playPauseBtn.textContent = '▶'
            videoPlayer.controls.playPauseBtn.classList.remove('playing')
        }
    })

    // Stop
    videoPlayer.controls.stopBtn?.addEventListener('click', () => {
        videoPlayer.video.pause()
        videoPlayer.video.currentTime = 0
        videoPlayer.controls.playPauseBtn.textContent = '▶'
        videoPlayer.controls.playPauseBtn.classList.remove('playing')
        videoPlayer.controls.seekFill.style.width = '0%'
        videoPlayer.controls.currentTime.textContent = '0:00'
    })

    // Seek bar
    videoPlayer.controls.seekBar?.addEventListener('click', (e) => {
        if (!videoPlayer.video.duration) return
        const rect = videoPlayer.controls.seekBar.getBoundingClientRect()
        videoPlayer.video.currentTime = ((e.clientX - rect.left) / rect.width) * videoPlayer.video.duration
    })

    // Speed
    videoPlayer.controls.speedSelect?.addEventListener('change', () => {
        videoPlayer.video.playbackRate = parseFloat(videoPlayer.controls.speedSelect.value)
    })

    // Video events
    videoPlayer.video.addEventListener('timeupdate', () => {
        if (!videoPlayer.video.duration) return
        const pct = (videoPlayer.video.currentTime / videoPlayer.video.duration) * 100
        videoPlayer.controls.seekFill.style.width = pct + '%'
        videoPlayer.controls.currentTime.textContent = formatTime(videoPlayer.video.currentTime)
    })

    videoPlayer.video.addEventListener('loadedmetadata', () => {
        videoPlayer.controls.duration.textContent = formatTime(videoPlayer.video.duration)
    })

    videoPlayer.video.addEventListener('ended', () => {
        videoPlayer.controls.playPauseBtn.textContent = '▶'
        videoPlayer.controls.playPauseBtn.classList.remove('playing')
        videoPlayer.controls.seekFill.style.width = '100%'
    })

    // Drag functionality for seek bar
    videoPlayer.controls.seekBar?.addEventListener('mousedown', (e) => {
        videoPlayer.isDragging = true
        seekVideo(e)
    })

    document.addEventListener('mousemove', (e) => {
        if (videoPlayer.isDragging) {
            seekVideo(e)
        }
    })

    document.addEventListener('mouseup', () => {
        videoPlayer.isDragging = false
    })

    // Touch support
    videoPlayer.controls.seekBar?.addEventListener('touchstart', (e) => {
        videoPlayer.isDragging = true
        seekVideoTouch(e)
    })

    document.addEventListener('touchmove', (e) => {
        if (videoPlayer.isDragging) {
            seekVideoTouch(e)
        }
    })

    document.addEventListener('touchend', () => {
        videoPlayer.isDragging = false
    })

    controlsContainer.classList.add('visible')
}

function seekVideo(e) {
    if (!videoPlayer.video.duration) return
    const rect = videoPlayer.controls.seekBar.getBoundingClientRect()
    videoPlayer.video.currentTime = ((e.clientX - rect.left) / rect.width) * videoPlayer.video.duration
}

function seekVideoTouch(e) {
    if (!videoPlayer.video.duration) return
    const rect = videoPlayer.controls.seekBar.getBoundingClientRect()
    const touch = e.touches[0]
    videoPlayer.video.currentTime = ((touch.clientX - rect.left) / rect.width) * videoPlayer.video.duration
}

function formatTime(seconds) {
    if (isNaN(seconds)) return '0:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
}

const exportObj = {
    addToStack,
    renderFXStack,
    updateOutput,
    clearOutput,
    generatePreview,
    renderVideo,
    downloadVideo
}

Object.keys(exportObj).forEach(key => {
    window[key] = exportObj[key]
})

// ----------------------------------------------------------------------------
// Bucket integration — drag frames/videos to bucket, drag images from bucket
// ----------------------------------------------------------------------------
const { bucket, dbGetAll } = initLegacyBucket({
    appName: 'framestack',
    onBucketDrop: (e) => {
        // Check for frame item drag (from frames-list)
        const frameId = e.dataTransfer.getData('application/x-framestack-frame')
        if (frameId) {
            const frame = frames.find(f => f.id === frameId)
            if (frame && frame.file && bucket) {
                // Read the image file and add to bucket
                frame.file.arrayBuffer().then(data => {
                    bucket.add({
                        source: 'framestack',
                        name: frame.name,
                        imageData: data,
                        imageFormat: frame.file.type || 'image/png',
                        imageMime: frame.file.type || 'image/png',
                        fileType: 'image'
                    }).catch(() => {})
                })
            }
            return
        }
        // Check for rendered video drag
        const renderId = e.dataTransfer.getData('application/x-framestack-render')
        if (renderId) {
            const render = renders.find(r => r.id === renderId)
            if (render && render.blob && bucket) {
                const format = qs('#output-format')?.value || 'webm'
                const ext = format === 'gif' ? 'gif' : format === 'mp4' ? 'mp4' : 'webm'
                render.blob.arrayBuffer().then(data => {
                    bucket.add({
                        source: 'framestack',
                        name: `${render.name.replace(/\s+/g, '_').toLowerCase()}.${ext}`,
                        videoData: data,
                        videoFormat: ext,
                        fileType: 'video'
                    }).catch(() => {})
                })
            }
            return
        }
    },
})

// Make frame items draggable to bucket
if (typeof renderFramesList === 'function') {
    const _origRenderFramesList = renderFramesList
    renderFramesList = function () {
        _origRenderFramesList()
        // Add dragstart to each frame item
        const items = qsa('#frames-list .frame-item')
        items.forEach((li, idx) => {
            const frame = frames[idx]
            if (!frame) return
            li.draggable = true
            li.addEventListener('dragstart', (e) => {
                e.dataTransfer.effectAllowed = 'copy'
                e.dataTransfer.setData('application/x-framestack-frame', frame.id)
                e.dataTransfer.setData('text/plain', frame.name)
            })
        })
    }
    // Re-render to apply draggable
    renderFramesList()
}

// Make rendered video tabs draggable to bucket
if (typeof switchTab === 'function') {
    const _origSwitchTab = switchTab
    switchTab = function (tabId) {
        _origSwitchTab(tabId)
        // Make video preview draggable
        const video = qs('#video-preview')
        if (video) {
            video.draggable = true
            video.addEventListener('dragstart', (e) => {
                e.dataTransfer.effectAllowed = 'copy'
                e.dataTransfer.setData('application/x-framestack-render', tabId)
                e.dataTransfer.setData('text/plain', tabId)
            })
        }
    }
}

// Allow dropping bucket images and videos onto the frames dropzone
if (typeof processImageFiles === 'function') {
    const dropzone = qs('#frames-dropzone')
    if (dropzone) {
        // Allow dragover for bucket items (the app's own handler only prevents default for files)
        dropzone.addEventListener('dragover', (e) => {
            if (e.dataTransfer.types.includes('application/x-bucket-item-id')) {
                e.preventDefault()
                e.dataTransfer.dropEffect = 'copy'
                dropzone.classList.add('dragover')
            }
        }, true)

        dropzone.addEventListener('drop', async (e) => {
            // Check for bucket item drop (image or video)
            const itemId = e.dataTransfer.getData('application/x-bucket-item-id')
            if (itemId && bucket) {
                e.preventDefault()
                e.stopPropagation()
                dropzone.classList.remove('dragover')
                try {
                    const dbItems = await dbGetAll(bucket.bucketId)
                    const item = dbItems.find(i => i.id === itemId)
                    if (item && item.wav) {
                        if (item.type === 'image') {
                            // Drop bucket image → add as frame
                            const ext = (item.name.split('.').pop() || 'png').toLowerCase()
                            const mime = {
                                png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg',
                                gif: 'image/gif', bmp: 'image/bmp', webp: 'image/webp'
                            }[ext] || 'image/png'
                            const blob = new Blob([item.wav], { type: mime })
                            const file = new File([blob], item.name, { type: mime })
                            processImageFiles([file])
                        } else if (item.type === 'video') {
                            // Drop bucket video → extract frames via FFmpeg
                            const ext = (item.name.split('.').pop() || 'mp4').toLowerCase()
                            const mime = {
                                mp4: 'video/mp4', webm: 'video/webm', ogv: 'video/ogg',
                                mov: 'video/quicktime', mkv: 'video/x-matroska',
                                avi: 'video/x-msvideo', flv: 'video/x-flv'
                            }[ext] || 'video/mp4'
                            const blob = new Blob([item.wav], { type: mime })
                            const file = new File([blob], item.name, { type: mime })
                            processVideoFiles([file])
                        }
                    }
                } catch (err) {
                }
            }
        }, true) // capture phase to intercept before the app's own handler
    }
}
